import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { GeoPricingService } from './geo-pricing.service';

@Injectable({
    providedIn: 'root'
})
export class LanguageService {
    private currentLanguageSubject = new BehaviorSubject<string>('en');
    public currentLanguage$ = this.currentLanguageSubject.asObservable();
    private router = inject(Router);
    private geoPricingService = inject(GeoPricingService);

    constructor(private translate: TranslateService) {
        // El idioma real se establece en initializeLanguage() vía APP_INITIALIZER
        this.translate.use('en');

        // Escuchar cambios de ruta para detectar idioma
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            this.detectLanguageFromUrl(event.urlAfterRedirects);
        });
    }

    /**
     * Detecta el idioma desde la URL actual
     */
    private detectLanguageFromUrl(url: string): void {
        const langMatch = url.match(/^\/(es|en)(\/|$)/);
        if (langMatch) {
            const detectedLang = langMatch[1];
            // Solo cambiar si es diferente al actual
            if (detectedLang !== this.currentLanguageSubject.value) {
                this.translate.use(detectedLang);
                this.currentLanguageSubject.next(detectedLang);
                // Guardar en localStorage
                if (typeof localStorage !== 'undefined') {
                    localStorage.setItem('selectedLanguage', detectedLang);
                }
            }
        }
    }

    /**
     * Obtiene el idioma desde la URL actual
     */
    getLanguageFromUrl(): string {
        const currentUrl = this.router.url;
        const langMatch = currentUrl.match(/^\/(es|en)(\/|$)/);
        return langMatch ? langMatch[1] : '';
    }

    /**
     * Cambia el idioma y navega a la nueva URL
     */
    setLanguage(lang: string): void {
        const currentLang = this.getCurrentLanguage();

        // Si el idioma es el mismo, no hacer nada
        if (lang === currentLang) {
            return;
        }

        // Cambiar idioma en translate
        this.translate.use(lang);
        this.currentLanguageSubject.next(lang);

        // Guardar el idioma seleccionado en localStorage
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem('selectedLanguage', lang);
        }

        // Navegar a la nueva URL con el prefijo de idioma correcto
        const currentUrl = this.router.url;
        let newUrl: string;

        if (currentUrl.startsWith(`/${currentLang}`)) {
            // Reemplazar el prefijo de idioma actual
            newUrl = currentUrl.replace(`/${currentLang}`, `/${lang}`);
        } else {
            // Si no hay prefijo, agregar el nuevo idioma
            newUrl = `/${lang}${currentUrl}`;
        }

        // Navegar sin recargar la página
        this.router.navigateByUrl(newUrl);
    }

    getCurrentLanguage(): string {
        return this.currentLanguageSubject.value;
    }

    getStoredLanguage(): string {
        if (typeof localStorage !== 'undefined') {
            return localStorage.getItem('selectedLanguage') || '';
        }
        return '';
    }

    /**
     * Inicializa el idioma con la siguiente prioridad:
     *  1. localStorage → el usuario lo cambió manualmente alguna vez
     *  2. GeoPricingService
     *  3. 'en' → default absoluto
     *
     * La URL ya llega con el prefijo correcto desde el redirect geo del servidor SSR.
     */
    initializeLanguage(): void {
        const storedLang = this.getStoredLanguage();
        const geoLang = this.geoPricingService.isSpanish() ? 'es' : 'en';
        const lang = storedLang || geoLang || 'en';

        this.translate.use(lang);
        this.currentLanguageSubject.next(lang);
    }
}